/* eslint-disable @nx/enforce-module-boundaries */
'use client';

import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { DigitalWalletsType } from '@payments-checkout/shared/plugins/analitica/analitica.types';
import {
  usePaymentMethod,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { Icon, Text, Tooltip } from '@stone-payments/jade';
import { useClickToPay } from 'modules/checkout/src/clickToPay/hooks/useClickToPayWalletData.hook';
import { PaymentFailedAlert } from 'modules/checkout/src/components/alert';
import { usePixFlow } from 'modules/checkout/src/flows/pixFlow';
import { JadeProviderSecondaryColor } from 'modules/checkout/src/providers/jadeProviderSecondaryColor';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ApplePay } from '../../../applePay/components/applePaySelectButton.component';
import { ClickToPay } from '../../../clickToPay/components/clickToPay.component';
import { PaymentMethodSelection } from '../../../components/paymentMethodSelection/paymentMethodSelection';
import { PaymentsDivider } from '../../../components/paymentsDivider';
import { SelectedPaymentMethod } from '../../../components/selectedPaymentMethod';
import { GooglePay } from '../../../googlePay/components/googlePaySelectionButton.component';
import { usePreselectVariant } from '../../../hooks/usePreselectVariant';
import { useSelectedPaymentMethod } from '../../../hooks/useSelectedPaymentMethod';
import { customerDataType } from '../../../types/customer.types';
import { type PaymentSettings } from '../../../types/paymentLink.types';
import { digitalWallets } from '../../types/digitalWallets.types';
import { DigitalWalletsSection } from '../digitalWallets/digitalWalletsSection.component';
import { CheckoutSubmitButton } from './checkoutSubmitButton.component';
import { PaymentDetailsSection } from './paymentDetailsSection.component';

declare global {
  interface Window {
    hst_disclaimer_span: (data: any) => void;
    hst_click_to_pay_info_box: () => void;
  }
}
type CustomerDataFormProps = {
  paymentSettings: PaymentSettings;
  customerSettings: {
    customer: customerDataType;
  };
  hasAnyDigitalWallet?: boolean;
};

export function CustomerDataForm(props: CustomerDataFormProps) {
  const intl = useIntl();
  const params = useParams<{ paymentLinkId: string }>();
  const { sessionId } = useSessionId();
  const {
    setPaymentWithClickToPay,
    setPaymentMethod,
    setSelectedDigitalWallet,
    paymentWithClickToPay,
    selectedDigitalWallet,
    isCustomerFormLoading,
    paymentMethod,
    clickToPayData,
    clickToPayLoading,
  } = usePaymentMethod();
  const { clickToPaySession, clickToPaySessionPending } = useClickToPay();
  const { showPaymentDetails, skipPreselect } = usePreselectVariant();
  const { selectedMethod, clearSelection } = useSelectedPaymentMethod();
  const showSelectedMethod = skipPreselect && !!selectedMethod;
  const { isReducedPix: isReducedPixFlow } = usePixFlow();
  const [isDigitalWalletValid, setIsDigitalWalletValid] = useState(false);
  const isReducedPix = paymentMethod === 'pix' && isReducedPixFlow;

  const renderablePaymentMethods = ['credit_card', 'pix', 'boleto'];
  const hasAvailablePaymentMethods = props.paymentSettings.accepted_payment_methods.some(
    (method) => method && renderablePaymentMethods.includes(method)
  );

  useEffect(() => {
    if (!clickToPayData && paymentWithClickToPay) {
      clickToPaySession();
    }
  }, [paymentWithClickToPay]);

  useEffect(() => {
    const clickToPayIsLoaded =
      !clickToPaySessionPending && !clickToPayLoading && paymentWithClickToPay;
    if (clickToPayIsLoaded && typeof window.hst_disclaimer_span === 'function') {
      window.hst_disclaimer_span({
        locale: 'pt_BR',
        brands: ['VISA', 'MASTERCARD'],
      });
    }
  }, [clickToPayLoading, clickToPaySessionPending]);

  const handleSelectedDigitalWallet = (digitalWallet: digitalWallets) => {
    const isClickToPay = digitalWallet === 'clickToPay';
    const clickToPayMessage = document.getElementById('hst_disclaimer_span');
    if (isCustomerFormLoading) return;
    if (clickToPayMessage) {
      clickToPayMessage.style.display = isClickToPay ? 'flex' : 'none';
    }
    setPaymentWithClickToPay(isClickToPay);
    setSelectedDigitalWallet(digitalWallet);
    setPaymentMethod(null);
    const digitalWalletPossibleValues = {
      applePay: 'applepay',
      googlePay: 'googlepay',
      clickToPay: 'clicktopay',
    };
    analiticaInstance.events.homePage.paymentMethodClicked(
      digitalWalletPossibleValues[digitalWallet] as DigitalWalletsType
    );
  };

  return (
    <div className="w-full flex">
      <div className="flex flex-col grow px-jade-200 gap-jade-150 mx-auto mt-jade-300 mb-jade-300 max-w-[416px] sm:mt-jade-600">
        {showSelectedMethod && (
          <div className="bg-jade-background-surface rounded-xl p-jade-250 min-500:p-0 min-500:rounded-none">
            <SelectedPaymentMethod
              method={selectedMethod}
              onEdit={clearSelection}
              disabled={isCustomerFormLoading}
            />
          </div>
        )}
        <div
          className={`bg-jade-background-surface rounded-xl p-jade-250 min-500:p-0 min-500:rounded-none ${
            showSelectedMethod ? 'hidden' : ''
          }`}
        >
          <JadeProviderSecondaryColor>
            <div className="flex flex-col gap-jade-150">
              {skipPreselect && (
                <div className="mb-jade-150">
                  <Text variant="heading-small" className="text-center">
                    {intl.formatMessage({ id: 'paymentMethod.form.selectTitle' })}
                  </Text>
                </div>
              )}
              {props.hasAnyDigitalWallet && (
                <>
                  <DigitalWalletsSection>
                    <ApplePay
                      setIsDigitalWalletsSelected={handleSelectedDigitalWallet}
                      active={selectedDigitalWallet === 'applePay'}
                    />
                    <GooglePay
                      setIsDigitalWalletsSelected={handleSelectedDigitalWallet}
                      active={selectedDigitalWallet === 'googlePay'}
                    />
                    <ClickToPay
                      setIsDigitalWalletsSelected={handleSelectedDigitalWallet}
                      active={selectedDigitalWallet === 'clickToPay'}
                    />
                  </DigitalWalletsSection>
                  {hasAvailablePaymentMethods && !skipPreselect && <PaymentsDivider />}
                </>
              )}
              <PaymentMethodSelection paymentSettings={props.paymentSettings} />
            </div>
          </JadeProviderSecondaryColor>
        </div>
        <PaymentDetailsSection
          customer={props.customerSettings?.customer}
          sessionId={sessionId ?? ''}
          paymentLinkId={params.paymentLinkId}
          selectedDigitalWallet={selectedDigitalWallet}
          clickToPaySessionPending={clickToPaySessionPending}
          isDigitalWalletValid={isDigitalWalletValid}
          setIsDigitalWalletValid={setIsDigitalWalletValid}
        />
        <div id="hst_disclaimer_span" />
        <PaymentFailedAlert />
        <div
          className={`w-full flex flex-col-reverse items-center mt-5 gap-jade-300 sm:flex-row ${
            showPaymentDetails ? 'justify-between' : 'justify-center'
          }`}
          data-testid="checkout-footer-page"
        >
          <div className="flex items-center gap-jade-100">
            <Icon color="medium" size="small" use="lock-close" />
            <Text
              color="medium"
              variant="text-medium"
              weight="regular"
              className="ml-jade-50 mr-jade-50 whitespace-nowrap"
            >
              {intl.formatMessage({ id: 'protectedPayment' })}
            </Text>
            <Tooltip
              placement="top"
              trigger={<Icon color="medium" size="small" use="circle-info" />}
              text={intl.formatMessage({ id: 'footer.tooltip' })}
            />
          </div>
          <CheckoutSubmitButton
            selectedDigitalWallet={selectedDigitalWallet}
            isDigitalWalletValid={isDigitalWalletValid}
            isCustomerFormLoading={isCustomerFormLoading}
            clickToPaySessionPending={clickToPaySessionPending}
            isReducedPix={isReducedPix}
          />
        </div>
      </div>
    </div>
  );
}
